<script lang="ts">
  import { orgClose } from "@nrk/origo";
  import { isEqual, nth } from "lodash-es";
  import { resetFilters, searchFilter, searchParams, updateSearchFilter } from "state/page";
  import {
    availableSourceSystems,
    availableRightsMarkers,
    type DateFilter,
    getDefaultSearchParams,
    type SearchFilter,
  } from "state/params";
  import { getFolderAncestry } from "services/folders/api";
  import { digitalSourceTypeGroupLabels, Mimir, usageDomains } from "bildebanken-model";
  import { rootFolderIds } from "services/folders/productions";
  import { format } from "date-fns";
  import { nb } from "date-fns/locale";
  import { getCollection } from "services/collections/api";

  type FilterKey = keyof SearchFilter;
  const listFormat = new Intl.ListFormat("nb", { style: "long", type: "conjunction" });

  $: pills = buildPills($searchFilter);
  $: folderId = $searchFilter.folderId;

  let folderLabel: string | undefined;

  type Pill = {
    key: FilterKey;
    label: string;
    value?: string; // Used for individual tag values
  };

  $: if (folderId) {
    updateFolderLabel(folderId);
  } else {
    folderLabel = undefined;
  }

  function mapDatePill(value: DateFilter) {
    const secondsBackMap: { [key: number]: string } = {
      3600: "timen",
      14400: "4 timer",
      86400: "24 timer",
      604800: "uken",
      2678400: "måneden",
    };
    let datePill;
    if (value.type === "Since") {
      datePill = secondsBackMap[value.secondsBack]
        ? value.label.replace(`${value.secondsBack}`, secondsBackMap[value.secondsBack])
        : value.label;
    } else {
      datePill = `fra ${value.from?.toLocaleDateString("no-NB")} til ${value.to?.toLocaleDateString(
        "no-NB",
      )}`;
    }
    if (value.field === "mediaCreatedOn" && !datePill.includes("Fotografert"))
      return `Fotografert ${datePill}`;
    else if (value.field === "createdOn" && !datePill.includes("Lastet opp"))
      return `Lastet opp ${datePill}`;
    else return datePill;
  }

  function isDateFilter(filter: DateFilter | any): filter is DateFilter {
    return filter.label && filter.field && (filter.type === "Range" || filter.type === "Since");
  }

  function buildPills(filter: SearchFilter) {
    return Object.entries(filter)
      .map(([key, value]) => mapPill(key as FilterKey, value))
      .flat()
      .filter(Boolean) as Pill[];
  }

  async function updateFolderLabel(folderId: string) {
    const pill = await mapFolderPill("folderId", folderId);

    if ($searchFilter.folderId === folderId) {
      folderLabel = pill.label;
    }
  }

  function getFolderPaths(ancestry: Mimir.AncestryResponse, folderId: string) {
    return ancestry[folderId].paths[folderId].pop();
  }
  function createFolderBreadCrumb(folders: { name: string; id: string }[]) {
    const productionSubfolder = folders?.findIndex(
      (folder) => folder.id === rootFolderIds.stage.productions,
    );

    if (productionSubfolder && productionSubfolder > 0) {
      const production = nth(folders, -1)?.name;
      const parent = nth(folders, -2);
      const formattedDate =
        (parent && format(new Date(parent.name), "d. MMMM", { locale: nb })) || "";

      return `Produksjon: ${production} ${formattedDate}`;
    }
    return folders
      ?.map((folder) => folder.name)
      ?.splice(3, 4)
      .join(" / ");
  }

  async function mapFolderPill<K extends FilterKey>(key: K, value: SearchFilter[K]) {
    const folderAncestry = await getFolderAncestry(`${value}`);
    const folders = getFolderPaths(folderAncestry, `${value}`);
    if (!folders) {
      return { key, label: value };
    }
    if (folders[folders.length - 2]?.name === "Samlinger") {
      const collectionData = await getCollection(`${value}`);
      return {
        key,
        label: `Samling: ${collectionData.metadata.displayName || collectionData.name}`,
      };
    }

    const label = createFolderBreadCrumb(folders);
    return { key, label };
  }

  function mapPill<K extends FilterKey>(key: K, value: SearchFilter[K]): Pill | Pill[] | undefined {
    if (key === "nrkImages") {
      if (value === true) {
        return { key, label: "NRK Bilder" };
      } else {
        return;
      }
    }
    if (key === "ntbCuratedImages") {
      if (value === true) {
        return { key, label: "Utvalgte bilder (NTB)" };
      } else {
        return;
      }
    }

    if (key === "photographer") {
      if (typeof value === "object" && "name" in value) {
        return { key, label: "Fotograf: " + value.name };
      }
    }
    if (key === "tags") {
      if (Array.isArray(value) && value.length > 0) {
        return value.map((tag) => ({ key, label: "Tagger: " + tag, value: tag }));
      }
    }

    if (key === "date") {
      if (typeof value === "object" && isDateFilter(value)) {
        return { key, label: mapDatePill(value) };
      }
    }

    if (
      key === "sourceSystems" &&
      !isEqual(value, getDefaultSearchParams().filter.sourceSystems) &&
      Array.isArray(value)
    ) {
      if (value.length > 0 && value.length < availableSourceSystems.length) {
        return { key, label: "Kilder: " + listFormat.format(value) };
      } else {
        return { key, label: "Alle kilder" };
      }
    }

    if (
      key === "digitalSourceTypeGroups" &&
      !isEqual(value, getDefaultSearchParams().filter.digitalSourceTypeGroups) &&
      Array.isArray(value) &&
      value.length > 0
    ) {
      if (value.length > 0 && value.length < digitalSourceTypeGroupLabels.length) {
        return { key, label: "Innholdstyper: " + listFormat.format(value) };
      } else {
        return { key, label: "Alle innholdstyper" };
      }
    }

    if (
      key === "usageDomains" &&
      !isEqual(value, getDefaultSearchParams().filter.usageDomains) &&
      Array.isArray(value) &&
      value.length > 0
    ) {
      if (value.length > 0 && value.length < usageDomains.length) {
        return {
          key,
          label:
            "Bruksområder: " +
            listFormat.format(
              value.map((v) => usageDomains.find((u) => u.value === v)?.name || ""),
            ),
        };
      } else {
        return { key, label: "Alle bruksområder" };
      }
    }

    function mapLabelsToRightsMarkers(value: string): string {
      const marker = availableRightsMarkers.find((marker) => marker.name === value);
      return marker ? marker.label : value;
    }
    if (
      key === "rightsMarker" &&
      !isEqual(value, getDefaultSearchParams().filter.rightsMarker) &&
      Array.isArray(value)
    ) {
      if (value.length > 0 && value.length < availableRightsMarkers.length) {
        return {
          key,
          label: "Bruksrettigheter: " + listFormat.format(value.map(mapLabelsToRightsMarkers)),
        };
      } else {
        return { key, label: "Alle bruksrettigheter" };
      }
    }

    if (key === "jobId") {
      if (value) {
        return { key, label: "Oppdrag: " + value };
      }
    }

    if (typeof value === "object" && "label" in value) {
      return {
        key,
        label: value.label,
      };
    }
    if (key === "owner") {
      if (value === "current-user") {
        return { key, label: "Mine opplastinger" };
      }
    }
  }

  function removePill(key: keyof SearchFilter, tagValue?: string) {
    if (key === "tags" && tagValue) {
      // Remove specific tag from the array
      const currentTags = $searchFilter.tags || [];
      const newTags = currentTags.filter((tag) => tag !== tagValue);
      updateSearchFilter(key, newTags.length > 0 ? newTags : undefined);
    } else if (key === "sourceSystems") {
      updateSearchFilter(key, getDefaultSearchParams().filter.sourceSystems);
    } else if (key === "digitalSourceTypeGroups") {
      updateSearchFilter(key, getDefaultSearchParams().filter.digitalSourceTypeGroups);
    } else if (key === "rightsMarker") {
      updateSearchFilter(key, getDefaultSearchParams().filter.rightsMarker);
    } else if (key === "usageDomains") {
      updateSearchFilter(key, getDefaultSearchParams().filter.usageDomains);
    } else if (key === "owner") {
      updateSearchFilter(key, undefined);
    } else {
      updateSearchFilter(key);
    }
  }

  $: folderPill = Object.entries($searchFilter);
</script>

<div class="search-pills">
  {#each pills as pill (pill.key + (pill.value || ""))}
    <button type="button" on:click={() => removePill(pill.key, pill.value)} class="org-tag">
      <span class="tag-content">
        {pill.label}
      </span>
      {@html orgClose}
    </button>
  {/each}
  {#if folderPill && folderLabel}
    <button type="button" on:click={() => removePill("folderId")} class="org-tag">
      {folderLabel}
      {@html orgClose}
    </button>
  {/if}
  {#if $searchParams.owner === "current-user"}
    <button type="button" on:click={() => updateSearchFilter("owner", undefined)} class="org-tag">
      Mine opplastinger
      {@html orgClose}
    </button>
  {/if}
  {#if pills.length > 0 || (folderPill && folderLabel) || $searchParams.owner === "current-user"}
    <button class="org-button" on:click={() => resetFilters()}> Nullstill </button>
  {/if}
</div>

<style>
  .search-pills {
    margin-right: 5px;
    display: flex;
    flex-flow: wrap;
  }
  button {
    display: flex;
    align-items: center;
    gap: var(--org-xsmall);
  }
  button :global(svg) {
    pointer-events: none;
    font-size: var(--org-small);
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    outline: none;
  }
  .tag-content {
    max-width: 37ch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
